/**
 * Whether this campaign can be sent, and if not, why not.
 *
 * The verdict lives here because **it is reached twice**: once in the browser,
 * where it disables Continue while the screen is being filled in, and once on
 * the server, which re-parses and re-validates everything a posted form claims
 * (PLAN §12.2). The browser's copy is convenience — it can be skipped by
 * turning off script, by an old bundle, or by posting the form by hand — so the
 * server's is the one that counts, and the two must not be able to disagree
 * about what is allowed.
 *
 * Pure by construction (PLAN §4).
 */

import { recipientPairs, resolveDuplicates, type CampaignInput } from './campaign-input.ts';
import type { DuplicateGroup } from './recipients.ts';
import { measureSms, type SmsMetrics } from './sms.ts';
import {
  MAX_MESSAGE_UNITS,
  checkTemplate,
  longestMessage,
  type RenderedMessage,
} from './template.ts';
import { validatePairs, type ValidationOutcome } from './validation.ts';

export type CampaignProblemName =
  /** More rows than `MAX_IMPORT_ROWS`. Refused whole, never trimmed. */
  | 'list-over-limit'
  /** Nothing in the list can be sent. */
  | 'list-empty'
  /** A number is claimed by more than one row and nobody has said which wins. */
  | 'list-undecided'
  /** Nothing written. Only said out loud on submit — see `submitted`. */
  | 'template-empty'
  /** No `%promo%`: every recipient would get an identical message. */
  | 'template-no-placeholder'
  /** Past the provider's hard ceiling, which it rejects rather than truncates. */
  | 'template-too-long';

export interface CampaignProblem {
  readonly name: CampaignProblemName;
  /** Substituted into the `{placeholders}` of the catalogue message. */
  readonly params: Readonly<Record<string, number>>;
}

export interface CampaignCheck {
  readonly outcome: ValidationOutcome;
  /** Whether the repeated-number question has been answered. */
  readonly decided: boolean;
  /** The worst case the list can produce, and what it measures. */
  readonly longest: RenderedMessage;
  readonly metrics: SmsMetrics;
  readonly problems: readonly CampaignProblem[];
  readonly ok: boolean;
}

export interface CheckOptions {
  readonly maxRows: number;
  /**
   * Whether this is a posted form rather than a screen being filled in.
   *
   * The difference is what an *empty* field means. While typing, an empty
   * template and an empty list are simply a screen nobody has used yet, and a
   * panel that complains before it has been used is a panel nobody reads — so
   * they block Continue and say nothing. On submit they are the reason the
   * campaign was refused, and the re-rendered page has to say so or it looks
   * like the button did nothing.
   */
  readonly submitted?: boolean;
}

export function checkCampaign(input: CampaignInput, options: CheckOptions): CampaignCheck {
  const outcome = validatePairs(recipientPairs(input), {
    maxRows: options.maxRows,
    duplicates: resolveDuplicates(input),
    keep: input.keep,
  });

  const decided = isDecided(input, outcome.duplicates);
  const longest = longestMessage(
    input.template,
    outcome.recipients.map((recipient) => recipient.promoCode),
  );

  const submitted = options.submitted ?? false;
  const problems = [
    ...listProblems(outcome, decided, submitted),
    ...templateProblems(input.template, longest, submitted),
  ];

  return {
    outcome,
    decided,
    longest,
    metrics: measureSms(longest.message),
    problems,
    // An empty template raises no problem — nothing has been typed yet — but is
    // not sendable either, so `checkTemplate` is asked as well as the problems.
    ok: problems.length === 0 && outcome.totals.valid > 0 && checkTemplate(input.template).ok,
  };
}

/**
 * Whether the repeated numbers have been settled (specification §16).
 *
 * `first` and `last` settle every group at once. `manual` settles a group only
 * when one of its lines was actually chosen — an unanswered group falls back to
 * the first row, which is a sensible thing to *show* and not a decision anybody
 * made.
 */
export function isDecided(input: CampaignInput, groups: readonly DuplicateGroup[]): boolean {
  if (groups.length === 0) return true;
  if (input.duplicates === '') return false;
  if (input.duplicates !== 'manual') return true;

  const keep = new Set(input.keep);
  return groups.every((group) => group.lines.some((line) => keep.has(line)));
}

/**
 * What the list itself refuses on.
 *
 * Rows that cannot be sent are deliberately **not** here. Showing them is §11.2
 * and blocking on them would be the panel deciding that three bad numbers out
 * of ten thousand are worth cancelling a campaign for, which §59 says is not
 * its call.
 */
export function listProblems(
  outcome: ValidationOutcome,
  decided: boolean,
  submitted = false,
): readonly CampaignProblem[] {
  const problems: CampaignProblem[] = [];

  if (outcome.overLimit) {
    problems.push({
      name: 'list-over-limit',
      params: { rows: outcome.totals.imported, limit: outcome.limit },
    });
  }
  // While typing, only once something has been pasted: an empty screen is not a
  // problem, it is a screen nobody has used yet, and Continue is off anyway.
  if ((submitted || outcome.totals.imported > 0) && outcome.totals.valid === 0) {
    problems.push({ name: 'list-empty', params: {} });
  }
  if (!decided) {
    problems.push({ name: 'list-undecided', params: { groups: outcome.duplicates.length } });
  }

  return problems;
}

/**
 * What the message refuses on.
 *
 * An empty template raises nothing: nothing has been typed, and a panel that
 * complains before it has been used is a panel nobody reads. It is still not
 * sendable — `ok` requires `checkTemplate` as well.
 */
export function templateProblems(
  template: string,
  longest: RenderedMessage,
  submitted = false,
): readonly CampaignProblem[] {
  const problems: CampaignProblem[] = [];
  const check = checkTemplate(template);

  if (submitted && !check.ok && check.problem === 'empty') {
    problems.push({ name: 'template-empty', params: {} });
  }
  if (template !== '' && !check.ok && check.problem === 'missing-placeholder') {
    problems.push({ name: 'template-no-placeholder', params: {} });
  }
  if (longest.length > MAX_MESSAGE_UNITS) {
    problems.push({
      name: 'template-too-long',
      params: { length: longest.length, limit: MAX_MESSAGE_UNITS },
    });
  }

  return problems;
}
